// Modele de donnees de la vue PRESTATAIRE commande-ms (v2).
// Source unique des codes de statut : table `status` du seeder
// subsequent/api/database/seeders/DatabaseSeeder.php (PARTAGEE acheteur + prestataire via la
// colonne type_compte). On reproduit l'enum localement (pas d'import cross-case) pour garder la
// case autonome. Gating sur l'ID, pas sur le nom : les libelles DB sont volatils (ex. 13 = "À
// notifier" en base, parfois affiche "À signer" cote UI). Memes ids/noms que la case acheteur MS
// (SuiviOperationMSV2) puisque la table est partagee ; seul le sens metier change selon le role.
export enum StatusId {
  Brouillon = 1, // Systeme
  ConsultationEnCours = 2, // Acheteur — consultation lancee
  ACandidater = 3, // Commande — le presta peut candidater / refuser
  AAnalyser = 4, // Acheteur — analyse des reponses
  EnAttenteAnalyse = 5, // Commande — candidature deposee, attente acheteur (lecture seule)
  Refuser = 6, // refus / non-retenue
  ASigner = 7, // mode automatique : acheteur a signer
  EnAttenteSignaturePrestataire = 8, // phase signature ouverte cote presta
  EnAttenteSignatureAcheteur = 9,
  ASignerPrestataire = 10, // mode automatique : prestataire a signer
  Valider = 11, // signe des deux cotes -> ouvre Messagerie + Evaluation
  Expirer = 12, // expiration (commande sans reponse dans le delai)
  ANotifier = 13, // "À signer" mode manuscrite (analyse cloturee cote acheteur)
  Annule = 14, // annulation
}

// Onglets PRESTATAIRE MS (cf. oracle SuiviDeCommande.tsx:108) : 6 onglets, ordre fixe.
export type TabKind =
  | 'historique'
  | 'consultation'
  | 'candidature'
  | 'questions_reponses'
  | 'notifications'
  | 'messagerie'
  | 'evaluation';

// Un onglet est une donnee, pas un libelle a re-parser ni un index a deviner.
export interface TabDescriptor {
  key: string;
  kind: TabKind;
  label: string;
  disabled: boolean;
  inactive: boolean;
}

// Reference du statut (table `status` partagee). Lue par id, jamais par nom.
export interface StatusRef {
  id?: number;
  nom?: string;
}

// Evenement du flux d'historique (onglet Historique).
export interface HistoriqueEvent {
  prefix?: string;
  type?: string;
  label?: string | null;
  message?: string;
  operateur?: string | null;
  destinataire?: number | string | null;
  ip_address?: string | null;
  created_at?: string;
  updated_at?: string;
}

// Operation (marche subsequent) telle que VUE par le prestataire : la commande la WRAPPE
// (commande.operation). Champs limites a ce que la case consomme. Forme confirmee sur l'oracle
// (SuiviDeCommande.tsx:924-927 : operation.nom / numero / lot_technique / id_lot_departement /
// lot_departement.departement.nom).
export interface OperationRef {
  id?: number;
  // Statut de l'OPERATION (distinct de commande.status, qui est le statut de la commande presta).
  // Load-bearing : pilote la fenetre d'envoi de la messagerie (TabMessagerie lit le statut operation).
  id_status?: number;
  status?: StatusRef;
  nom?: string;
  nom_ouvrage?: string;
  numero?: string | number;
  reference?: string;
  besoin?: string;
  type?: string;
  adresse?: string;
  code_postal?: string;
  ville?: string;
  visite?: string | number | boolean;
  condition_visite?: string;
  coordonnees_maitrise?: string;
  coordonnees_maitrise_ouvrage?: string;
  date_debut_operation?: string;
  date_fin_operation?: string;
  expire_at?: string;
  reponse_technique?: string | null;
  lot_technique?: { id?: number | string; nom?: string };
  id_lot_departement?: number | string;
  lot_departement?: { id_departement?: number; departement?: { id?: number | string; nom?: string } };
  organisme?: { id?: number; nom?: string };
  acheteur?: { prenom?: string; nom?: string; email_contact?: string; telephone?: string };
  [key: string]: unknown;
}

// La commande MS cote prestataire (reponse GET /subsequents/commande/{id} -> {status, data}).
// Champs connus declares explicitement ; l'index signature reste un filet `unknown` (jamais
// `any`) pour absorber la reponse enrichie (contrat, criteres, articles...) sans la sur-typer
// avant d'en avoir besoin (les onglets affineront leur propre vue-modele).
export interface CommandeMS {
  id: number;
  id_status?: number;
  status?: StatusRef;
  // Conserve pour le typage de la reponse API, mais volontairement NON affiche : l'oracle MS ne
  // montre pas de rang dans le sousTitre (contrairement a BC). Parite oracle MS.
  rang?: number;
  expire_at?: string | null;
  created_at?: string;
  updated_at?: string;
  id_entite?: number;
  id_contrat?: number;
  // Horodatage de l'attestation "documents administratifs a jour" (preuve datee de l'engagement).
  // Non-null = atteste ; null = pas/plus atteste. Le front en derive l'etat coche initial de la case
  // d'attestation (regle 6). Pose par la route dediee attestation-documents et redate au depot.
  attestation_documents_at?: string | null;
  historique?: HistoriqueEvent[];
  // Depot de l'offre : enrichi par core depuis la preuve de signature prestataire. `nom` est la
  // PERSONNE qui a signe (pas l'entite), `date` un horodatage ISO. null tant que rien n'est depose.
  depot?: { nom?: string | null; date?: string | null } | null;
  // Notification du marche : enrichi par core depuis la preuve de signature ACHETEUR (sa signature
  // vaut notification). Meme forme que `depot`. null tant que le marche n'est pas notifie.
  notification?: { nom?: string | null; date?: string | null } | null;
  operation?: OperationRef;
  // Present cote operation (acheteur) ; absent cote commande presta -> sert a SuiviDuMarche pour
  // distinguer 'operation' vs 'commande'. Declare pour le typage, non requis.
  commandes?: unknown[];
  contrat?: { entite?: Record<string, unknown> };
  // Signataire designe du cote prestataire (onglet Notifications et signatures du marche).
  prestataire_signataire?: {
    id_user?: number | string;
    nom?: string;
    prenom?: string;
    email_contact?: string;
  };
  reponse_technique?: string | null;
  // Reponse technique au besoin (texte libre presta). Le backend la renvoie sous `reponse`.
  reponse?: string;
  // Criteres techniques : le backend les stocke en `note_technique` (3 champs description_*).
  // L'onglet Candidature les derive en { organisation, moyens, social }.
  note_technique?: {
    description_operationnel?: string;
    description_humain?: string;
    description_environnement?: string;
  };
  criteres?: unknown;
  // Lignes financieres (DPGF) : article + son prix via le pivot de la relation commande<->article.
  articles?: Array<{
    id?: number | string;
    code?: string;
    nom?: string;
    numero?: string | number;
    pivot?: { prix?: number | string };
  }>;
  articlesPrestataire?: unknown[];
  // Pas de champ `formulaire` cote MS : contrairement a BC (ou EntityService.adaptApiData le
  // produit), getCommandeMs renvoie la commande brute. La candidature lira directement
  // operation.* + reponse / note_technique / articles (etape 3).
  [key: string]: unknown;
}
